// AUTO-STUB GENERATION for missing imports
// Scans all imports (relative + @/ alias), detects missing files, generates smart stubs

export interface ImportInfo {
  sourceFile: string;
  importPath: string;
  resolvedPath: string;
  namedExports: string[];
  defaultExport: string | null;
  importType: 'relative' | 'alias';
}

export interface MissingImport extends ImportInfo {
  suggestedFilePath: string;
}

// File extensions to skip (non-code imports)
const SKIP_EXTENSIONS =
  /\.(css|scss|sass|less|svg|png|jpg|jpeg|gif|webp|ico|woff|woff2|ttf|eot|mp4|webm|mp3|wav|json)$/i;

// Extensions to try when resolving imports
const RESOLVE_EXTENSIONS = [
  '.tsx',
  '.ts',
  '.jsx',
  '.js',
  '/index.tsx',
  '/index.ts',
  '/index.jsx',
  '/index.js',
];

/**
 * Scan all imports across a set of files.
 * Handles both relative (./  ../) and alias (@/) imports.
 */
export function scanAllImports(files: Array<{ filename: string; code: string }>): ImportInfo[] {
  const results: ImportInfo[] = [];

  // Regex for relative imports: import ... from './...' or '../...'
  const relativeImportRe =
    /import\s+(?:([\w$]+)(?:\s*,\s*)?)?(?:\{([^}]*)\})?\s*from\s*["'](\.[^"']+)["']/g;
  // Regex for alias imports: import ... from '@/...'
  const aliasImportRe =
    /import\s+(?:([\w$]+)(?:\s*,\s*)?)?(?:\{([^}]*)\})?\s*from\s*["'](@\/[^"']+)["']/g;
  // Also catch side-effect imports: import './something' or import '@/something'
  const sideEffectRelRe = /import\s+["'](\.[^"']+)["']/g;
  const sideEffectAliasRe = /import\s+["'](@\/[^"']+)["']/g;

  for (const file of files) {
    if (!/\.(tsx?|jsx?)$/.test(file.filename)) continue;

    const fileDir = file.filename.replace(/\/[^/]+$/, '') || '.';

    // --- Relative imports ---
    let match: RegExpExecArray | null;
    relativeImportRe.lastIndex = 0;
    while ((match = relativeImportRe.exec(file.code)) !== null) {
      const defaultExport = match[1] || null;
      const namedRaw = match[2] || '';
      const importPath = match[3];

      if (SKIP_EXTENSIONS.test(importPath)) continue;

      const namedExports = namedRaw
        .split(',')
        .map((s) =>
          s
            .trim()
            .split(/\s+as\s+/)[0]
            .trim(),
        )
        .filter(Boolean);

      const resolvedPath = resolveRelativePath(fileDir, importPath);

      results.push({
        sourceFile: file.filename,
        importPath,
        resolvedPath,
        namedExports,
        defaultExport,
        importType: 'relative',
      });
    }

    // Side-effect relative imports
    sideEffectRelRe.lastIndex = 0;
    while ((match = sideEffectRelRe.exec(file.code)) !== null) {
      const importPath = match[1];
      if (SKIP_EXTENSIONS.test(importPath)) continue;
      // Skip if already captured by the main regex (check if this line also has 'from')
      const line = getLineContaining(file.code, match.index);
      if (line.includes(' from ')) continue;

      const resolvedPath = resolveRelativePath(fileDir, importPath);
      results.push({
        sourceFile: file.filename,
        importPath,
        resolvedPath,
        namedExports: [],
        defaultExport: null,
        importType: 'relative',
      });
    }

    // --- Alias imports (@/) ---
    aliasImportRe.lastIndex = 0;
    while ((match = aliasImportRe.exec(file.code)) !== null) {
      const defaultExport = match[1] || null;
      const namedRaw = match[2] || '';
      const importPath = match[3];

      if (SKIP_EXTENSIONS.test(importPath)) continue;

      const namedExports = namedRaw
        .split(',')
        .map((s) =>
          s
            .trim()
            .split(/\s+as\s+/)[0]
            .trim(),
        )
        .filter(Boolean);

      // @/components/ui/select → src/components/ui/select
      const resolvedPath = importPath.replace(/^@\//, 'src/');

      results.push({
        sourceFile: file.filename,
        importPath,
        resolvedPath,
        namedExports,
        defaultExport,
        importType: 'alias',
      });
    }

    // Side-effect alias imports
    sideEffectAliasRe.lastIndex = 0;
    while ((match = sideEffectAliasRe.exec(file.code)) !== null) {
      const importPath = match[1];
      if (SKIP_EXTENSIONS.test(importPath)) continue;
      const line = getLineContaining(file.code, match.index);
      if (line.includes(' from ')) continue;

      const resolvedPath = importPath.replace(/^@\//, 'src/');
      results.push({
        sourceFile: file.filename,
        importPath,
        resolvedPath,
        namedExports: [],
        defaultExport: null,
        importType: 'alias',
      });
    }
  }

  return results;
}

/**
 * Filter imports to only those with no matching file in the known file set.
 */
export function findMissingImports(
  imports: ImportInfo[],
  existingFiles: Set<string>,
): MissingImport[] {
  const missing: MissingImport[] = [];
  const seen = new Set<string>();

  for (const imp of imports) {
    const resolved = imp.resolvedPath;

    // Skip if we already determined this path
    if (seen.has(resolved)) continue;

    // Check if file exists with any extension variant
    let found = false;

    // Check exact match first (might already have extension)
    if (existingFiles.has(resolved) || existingFiles.has(resolved.toLowerCase())) {
      found = true;
    }

    // Try extension fallbacks
    if (!found) {
      for (const ext of RESOLVE_EXTENSIONS) {
        const candidate = resolved + ext;
        if (existingFiles.has(candidate) || existingFiles.has(candidate.toLowerCase())) {
          found = true;
          break;
        }
      }
    }

    // Also check without src/ prefix (some files may be stored without it)
    if (!found && resolved.startsWith('src/')) {
      const withoutSrc = resolved.slice(4);
      if (existingFiles.has(withoutSrc)) found = true;
      if (!found) {
        for (const ext of RESOLVE_EXTENSIONS) {
          if (existingFiles.has(withoutSrc + ext)) {
            found = true;
            break;
          }
        }
      }
    }

    if (!found) {
      seen.add(resolved);
      // Determine the file path to create
      let suggestedFilePath = resolved;
      if (!suggestedFilePath.startsWith('src/')) {
        suggestedFilePath = 'src/' + suggestedFilePath;
      }
      // Add .tsx extension if none present
      if (!/\.\w+$/.test(suggestedFilePath)) {
        suggestedFilePath += '.tsx';
      }

      missing.push({ ...imp, suggestedFilePath });
    }
  }

  return missing;
}

/**
 * Generate a smart stub based on the import's path pattern and export requirements.
 */
export function generateStub(imp: MissingImport): string {
  const filePath = imp.suggestedFilePath;
  const fileName = filePath.split('/').pop() || '';
  const baseName = fileName.replace(/\.(tsx?|jsx?)$/, '');

  // Check if this is a shadcn UI component
  if (/components\/ui\//.test(filePath)) {
    const componentName = baseName.toLowerCase();
    if (SHADCN_STUB_REGISTRY[componentName]) {
      return SHADCN_STUB_REGISTRY[componentName];
    }
  }

  // Hook pattern: hooks/use* or use*.ts
  if (/hooks\//.test(filePath) || /^use[A-Z]/.test(baseName)) {
    return generateHookStub(baseName, imp.namedExports, imp.defaultExport);
  }

  // Utility/lib pattern
  if (
    /\/(lib|utils|helpers)\//.test(filePath) ||
    (/\.(ts|js)$/.test(filePath) && !/\.(tsx|jsx)$/.test(filePath))
  ) {
    return generateUtilStub(baseName, imp.namedExports, imp.defaultExport);
  }

  // Page pattern
  if (/pages?\//.test(filePath)) {
    return generatePageStub(baseName, imp.namedExports, imp.defaultExport);
  }

  // Default: component stub
  return generateComponentStub(baseName, imp.namedExports, imp.defaultExport);
}

// ── Stub generators by type ──

function generateComponentStub(name: string, named: string[], defaultExp: string | null): string {
  const componentName = toPascalCase(defaultExp || name);
  const lines = [
    '// AUTO-GENERATED STUB — will be replaced by AI',
    'import * as React from "react";',
    '',
  ];

  // Generate named exports as sub-components or re-exports
  for (const exp of named) {
    if (exp === componentName) continue; // will be the main component
    if (/^[A-Z]/.test(exp)) {
      // Looks like a component
      lines.push(
        `export function ${exp}({ children, className, ...props }: { children?: React.ReactNode; className?: string; [key: string]: any }) {`,
        `  return <div className={className} {...props}>{children}</div>;`,
        `}`,
        '',
      );
    } else {
      // Looks like a constant or function
      lines.push(`export const ${exp} = null as any;`, '');
    }
  }

  // Main component (default export)
  lines.push(
    `export function ${componentName}({ children, className, ...props }: { children?: React.ReactNode; className?: string; [key: string]: any }) {`,
    `  return (`,
    `    <div className={className} {...props}>`,
    `      {children || <p style={{ padding: "2rem", textAlign: "center", opacity: 0.5 }}>`,
    `        ${componentName} — placeholder`,
    `      </p>}`,
    `    </div>`,
    `  );`,
    `}`,
    '',
    `export default ${componentName};`,
    '',
  );

  return lines.join('\n');
}

function generatePageStub(name: string, named: string[], defaultExp: string | null): string {
  const pageName = toPascalCase(defaultExp || name);
  const lines = [
    '// AUTO-GENERATED STUB — will be replaced by AI',
    'import * as React from "react";',
    '',
  ];

  for (const exp of named) {
    if (exp === pageName) continue;
    lines.push(`export const ${exp} = null as any;`, '');
  }

  lines.push(
    `export default function ${pageName}() {`,
    `  return (`,
    `    <div style={{ padding: "2rem" }}>`,
    `      <h1 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "1rem" }}>${pageName}</h1>`,
    `      <p style={{ opacity: 0.6 }}>This page is a placeholder and will be filled in by the AI.</p>`,
    `    </div>`,
    `  );`,
    `}`,
    '',
  );

  return lines.join('\n');
}

function generateHookStub(name: string, named: string[], defaultExp: string | null): string {
  const hookName = defaultExp || (name.startsWith('use') ? name : `use${toPascalCase(name)}`);
  const lines = ['// AUTO-GENERATED STUB — will be replaced by AI', ''];

  // If there are named exports, export them
  for (const exp of named) {
    if (exp === hookName) continue;
    if (exp.startsWith('use')) {
      lines.push(`export function ${exp}() { return null as any; }`, '');
    } else {
      lines.push(`export const ${exp} = null as any;`, '');
    }
  }

  lines.push(
    `export function ${hookName}() {`,
    `  return {} as any;`,
    `}`,
    '',
    `export default ${hookName};`,
    '',
  );

  return lines.join('\n');
}

function generateUtilStub(name: string, named: string[], defaultExp: string | null): string {
  const lines = ['// AUTO-GENERATED STUB — will be replaced by AI', ''];

  for (const exp of named) {
    lines.push(`export const ${exp} = null as any;`, '');
  }

  if (defaultExp) {
    lines.push(`const ${defaultExp} = {} as any;`, `export default ${defaultExp};`, '');
  } else {
    lines.push('export default {};', '');
  }

  return lines.join('\n');
}

// ── SHADCN_STUB_REGISTRY ──
// Working HTML/Tailwind stubs for commonly used shadcn components that aren't bundled.
// These render valid UI without needing Radix or other dependencies.

export const SHADCN_STUB_REGISTRY: Record<string, string> = {
  select: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Select = ({ children, onValueChange, defaultValue, value }: { children?: React.ReactNode; onValueChange?: (v: string) => void; defaultValue?: string; value?: string }) => {
  const [val, setVal] = React.useState(value || defaultValue || "");
  React.useEffect(() => { if (value !== undefined) setVal(value); }, [value]);
  return <div data-stub="select">{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _value: val, _onChange: (v: string) => { setVal(v); onValueChange?.(v); } }) : c)}</div>;
};
const SelectTrigger = React.forwardRef<HTMLButtonElement, any>(({ className, children, _value, ...props }, ref) => (
  <button ref={ref} className={cn("flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring", className)} {...props}>{children}</button>
));
SelectTrigger.displayName = "SelectTrigger";
const SelectValue = ({ placeholder, _value }: { placeholder?: string; _value?: string }) => <span>{_value || placeholder || ""}</span>;
const SelectContent = ({ children, ...props }: any) => <div className="relative mt-1 rounded-md border bg-popover shadow-md" {...props}>{children}</div>;
const SelectItem = ({ children, value, _onChange, className, ...props }: any) => (
  <div className={cn("relative flex cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm hover:bg-accent", className)} onClick={() => _onChange?.(value)} {...props}>{children}</div>
);
const SelectGroup = ({ children }: any) => <div>{children}</div>;
const SelectLabel = ({ children, className }: any) => <div className={cn("py-1.5 px-2 text-sm font-semibold", className)}>{children}</div>;
const SelectSeparator = () => <div className="h-px my-1 bg-border" />;
export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel, SelectSeparator };
`,

  dialog: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Dialog = ({ children, open, onOpenChange }: { children?: React.ReactNode; open?: boolean; onOpenChange?: (open: boolean) => void }) => {
  const [isOpen, setIsOpen] = React.useState(open ?? false);
  React.useEffect(() => { if (open !== undefined) setIsOpen(open); }, [open]);
  const toggle = (v: boolean) => { setIsOpen(v); onOpenChange?.(v); };
  return <>{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _open: isOpen, _toggle: toggle }) : c)}</>;
};
const DialogTrigger = ({ children, _toggle, asChild, ...props }: any) => <span onClick={() => _toggle?.(true)} {...props}>{children}</span>;
const DialogContent = React.forwardRef<HTMLDivElement, any>(({ children, className, _open, _toggle, ...props }, ref) => {
  if (!_open) return null;
  return (<>
    <div className="fixed inset-0 z-50 bg-black/80" onClick={() => _toggle?.(false)} />
    <div ref={ref} className={cn("fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background p-6 shadow-lg", className)} {...props}>
      {children}
      <button className="absolute right-4 top-4 opacity-70 hover:opacity-100" onClick={() => _toggle?.(false)}>✕</button>
    </div>
  </>);
});
DialogContent.displayName = "DialogContent";
const DialogHeader = ({ className, ...props }: any) => <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />;
const DialogFooter = ({ className, ...props }: any) => <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4", className)} {...props} />;
const DialogTitle = React.forwardRef<HTMLHeadingElement, any>(({ className, ...props }, ref) => <h2 ref={ref} className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />);
DialogTitle.displayName = "DialogTitle";
const DialogDescription = React.forwardRef<HTMLParagraphElement, any>(({ className, ...props }, ref) => <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />);
DialogDescription.displayName = "DialogDescription";
const DialogClose = ({ children, _toggle, ...props }: any) => <span onClick={() => _toggle?.(false)} {...props}>{children}</span>;
const DialogOverlay = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => <div ref={ref} className={cn("fixed inset-0 z-50 bg-black/80", className)} {...props} />);
DialogOverlay.displayName = "DialogOverlay";
const DialogPortal = ({ children }: any) => <>{children}</>;
export { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription, DialogClose, DialogOverlay, DialogPortal };
`,

  tabs: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Tabs = ({ children, defaultValue, value, onValueChange, className, ...props }: any) => {
  const [active, setActive] = React.useState(value || defaultValue || "");
  React.useEffect(() => { if (value !== undefined) setActive(value); }, [value]);
  const change = (v: string) => { setActive(v); onValueChange?.(v); };
  return <div className={className} {...props}>{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _active: active, _change: change }) : c)}</div>;
};
const TabsList = React.forwardRef<HTMLDivElement, any>(({ className, children, _active, _change, ...props }, ref) => (
  <div ref={ref} className={cn("inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground", className)} {...props}>
    {React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _active, _change }) : c)}
  </div>
));
TabsList.displayName = "TabsList";
const TabsTrigger = React.forwardRef<HTMLButtonElement, any>(({ className, value, children, _active, _change, ...props }, ref) => (
  <button ref={ref} className={cn("inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all", _active === value ? "bg-background text-foreground shadow-sm" : "", className)} onClick={() => _change?.(value)} {...props}>{children}</button>
));
TabsTrigger.displayName = "TabsTrigger";
const TabsContent = React.forwardRef<HTMLDivElement, any>(({ className, value, children, _active, ...props }, ref) => {
  if (_active !== value) return null;
  return <div ref={ref} className={cn("mt-2", className)} {...props}>{children}</div>;
});
TabsContent.displayName = "TabsContent";
export { Tabs, TabsList, TabsTrigger, TabsContent };
`,

  'dropdown-menu': `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const DropdownMenu = ({ children, open, onOpenChange }: any) => {
  const [isOpen, setIsOpen] = React.useState(open ?? false);
  React.useEffect(() => { if (open !== undefined) setIsOpen(open); }, [open]);
  const toggle = (v: boolean) => { setIsOpen(v); onOpenChange?.(v); };
  return <div className="relative inline-block">{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _open: isOpen, _toggle: toggle }) : c)}</div>;
};
const DropdownMenuTrigger = ({ children, _toggle, asChild, ...props }: any) => <span onClick={() => _toggle?.((p: boolean) => !p)} {...props}>{children}</span>;
const DropdownMenuContent = React.forwardRef<HTMLDivElement, any>(({ children, className, _open, _toggle, align, ...props }, ref) => {
  if (!_open) return null;
  return <><div className="fixed inset-0 z-40" onClick={() => _toggle?.(false)} /><div ref={ref} className={cn("absolute z-50 min-w-[8rem] rounded-md border bg-popover p-1 text-popover-foreground shadow-md", className)} {...props}>{children}</div></>;
});
DropdownMenuContent.displayName = "DropdownMenuContent";
const DropdownMenuItem = React.forwardRef<HTMLDivElement, any>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent", className)} {...props}>{children}</div>
));
DropdownMenuItem.displayName = "DropdownMenuItem";
const DropdownMenuLabel = ({ className, children, ...props }: any) => <div className={cn("px-2 py-1.5 text-sm font-semibold", className)} {...props}>{children}</div>;
const DropdownMenuSeparator = ({ className, ...props }: any) => <div className={cn("h-px my-1 bg-border", className)} {...props} />;
const DropdownMenuGroup = ({ children }: any) => <div>{children}</div>;
const DropdownMenuCheckboxItem = DropdownMenuItem;
const DropdownMenuRadioGroup = ({ children }: any) => <div>{children}</div>;
const DropdownMenuRadioItem = DropdownMenuItem;
const DropdownMenuSub = ({ children }: any) => <div>{children}</div>;
const DropdownMenuSubTrigger = DropdownMenuItem;
const DropdownMenuSubContent = DropdownMenuContent;
const DropdownMenuPortal = ({ children }: any) => <>{children}</>;
const DropdownMenuShortcut = ({ className, ...props }: any) => <span className={cn("ml-auto text-xs tracking-widest opacity-60", className)} {...props} />;
export { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuGroup, DropdownMenuCheckboxItem, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal, DropdownMenuShortcut };
`,

  accordion: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Accordion = ({ children, type, className, ...props }: any) => <div className={cn("space-y-1", className)} {...props}>{children}</div>;
const AccordionItem = React.forwardRef<HTMLDivElement, any>(({ children, className, value, ...props }, ref) => {
  const [open, setOpen] = React.useState(false);
  return <div ref={ref} className={cn("border-b", className)} {...props}>{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _open: open, _toggle: () => setOpen(!open) }) : c)}</div>;
});
AccordionItem.displayName = "AccordionItem";
const AccordionTrigger = React.forwardRef<HTMLButtonElement, any>(({ children, className, _open, _toggle, ...props }, ref) => (
  <button ref={ref} className={cn("flex flex-1 w-full items-center justify-between py-4 font-medium transition-all hover:underline", className)} onClick={_toggle} {...props}>{children}<span className={cn("h-4 w-4 transition-transform", _open && "rotate-180")}>▼</span></button>
));
AccordionTrigger.displayName = "AccordionTrigger";
const AccordionContent = React.forwardRef<HTMLDivElement, any>(({ children, className, _open, ...props }, ref) => {
  if (!_open) return null;
  return <div ref={ref} className={cn("pb-4 pt-0 text-sm", className)} {...props}>{children}</div>;
});
AccordionContent.displayName = "AccordionContent";
export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
`,

  sheet: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Sheet = ({ children, open, onOpenChange }: any) => {
  const [isOpen, setIsOpen] = React.useState(open ?? false);
  React.useEffect(() => { if (open !== undefined) setIsOpen(open); }, [open]);
  const toggle = (v: boolean) => { setIsOpen(v); onOpenChange?.(v); };
  return <>{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _open: isOpen, _toggle: toggle }) : c)}</>;
};
const SheetTrigger = ({ children, _toggle, ...props }: any) => <span onClick={() => _toggle?.(true)} {...props}>{children}</span>;
const SheetContent = React.forwardRef<HTMLDivElement, any>(({ children, className, side = "right", _open, _toggle, ...props }, ref) => {
  if (!_open) return null;
  const sideClass = side === "left" ? "left-0" : side === "top" ? "top-0 inset-x-0" : side === "bottom" ? "bottom-0 inset-x-0" : "right-0";
  return (<>
    <div className="fixed inset-0 z-50 bg-black/80" onClick={() => _toggle?.(false)} />
    <div ref={ref} className={cn("fixed z-50 bg-background p-6 shadow-lg", sideClass, (side === "left" || side === "right") ? "inset-y-0 w-3/4 max-w-sm" : "h-auto", className)} {...props}>
      {children}
      <button className="absolute right-4 top-4 opacity-70 hover:opacity-100" onClick={() => _toggle?.(false)}>✕</button>
    </div>
  </>);
});
SheetContent.displayName = "SheetContent";
const SheetHeader = ({ className, ...props }: any) => <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />;
const SheetFooter = ({ className, ...props }: any) => <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4", className)} {...props} />;
const SheetTitle = React.forwardRef<HTMLHeadingElement, any>(({ className, ...props }, ref) => <h3 ref={ref} className={cn("text-lg font-semibold", className)} {...props} />);
SheetTitle.displayName = "SheetTitle";
const SheetDescription = React.forwardRef<HTMLParagraphElement, any>(({ className, ...props }, ref) => <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />);
SheetDescription.displayName = "SheetDescription";
const SheetClose = ({ children, _toggle, ...props }: any) => <span onClick={() => _toggle?.(false)} {...props}>{children}</span>;
const SheetOverlay = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => <div ref={ref} className={cn("fixed inset-0 z-50 bg-black/80", className)} {...props} />);
SheetOverlay.displayName = "SheetOverlay";
const SheetPortal = ({ children }: any) => <>{children}</>;
export { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription, SheetClose, SheetOverlay, SheetPortal };
`,

  popover: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Popover = ({ children, open, onOpenChange }: any) => {
  const [isOpen, setIsOpen] = React.useState(open ?? false);
  React.useEffect(() => { if (open !== undefined) setIsOpen(open); }, [open]);
  const toggle = (v: boolean) => { setIsOpen(v); onOpenChange?.(v); };
  return <div className="relative inline-block">{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _open: isOpen, _toggle: toggle }) : c)}</div>;
};
const PopoverTrigger = React.forwardRef<HTMLButtonElement, any>(({ children, _toggle, asChild, ...props }, ref) => <span ref={ref as any} onClick={() => _toggle?.((p: boolean) => !p)} {...props}>{children}</span>);
PopoverTrigger.displayName = "PopoverTrigger";
const PopoverContent = React.forwardRef<HTMLDivElement, any>(({ children, className, _open, _toggle, align, ...props }, ref) => {
  if (!_open) return null;
  return <><div className="fixed inset-0 z-40" onClick={() => _toggle?.(false)} /><div ref={ref} className={cn("absolute z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md", className)} {...props}>{children}</div></>;
});
PopoverContent.displayName = "PopoverContent";
const PopoverAnchor = ({ children }: any) => <>{children}</>;
export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
`,

  separator: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Separator = React.forwardRef<HTMLDivElement, any>(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => (
    <div
      ref={ref}
      role={decorative ? "none" : "separator"}
      aria-orientation={orientation}
      className={cn("shrink-0 bg-border", orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]", className)}
      {...props}
    />
  )
);
Separator.displayName = "Separator";
export { Separator };
`,

  avatar: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Avatar = React.forwardRef<HTMLSpanElement, any>(({ className, ...props }, ref) => (
  <span ref={ref} className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)} {...props} />
));
Avatar.displayName = "Avatar";
const AvatarImage = React.forwardRef<HTMLImageElement, any>(({ className, src, alt, ...props }, ref) => (
  <img ref={ref} src={src} alt={alt} className={cn("aspect-square h-full w-full", className)} {...props} />
));
AvatarImage.displayName = "AvatarImage";
const AvatarFallback = React.forwardRef<HTMLSpanElement, any>(({ className, children, ...props }, ref) => (
  <span ref={ref} className={cn("flex h-full w-full items-center justify-center rounded-full bg-muted", className)} {...props}>{children}</span>
));
AvatarFallback.displayName = "AvatarFallback";
export { Avatar, AvatarImage, AvatarFallback };
`,

  'scroll-area': `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const ScrollArea = React.forwardRef<HTMLDivElement, any>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("relative overflow-auto", className)} {...props}>{children}</div>
));
ScrollArea.displayName = "ScrollArea";
const ScrollBar = ({ orientation = "vertical", className }: any) => null;
export { ScrollArea, ScrollBar };
`,

  checkbox: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Checkbox = React.forwardRef<HTMLInputElement, any>(({ className, checked, onCheckedChange, ...props }, ref) => (
  <input
    ref={ref}
    type="checkbox"
    checked={checked}
    onChange={(e) => onCheckedChange?.(e.target.checked)}
    className={cn("h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)}
    {...props}
  />
));
Checkbox.displayName = "Checkbox";
export { Checkbox };
`,

  'radio-group': `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const RadioGroup = React.forwardRef<HTMLDivElement, any>(({ className, value, onValueChange, children, ...props }, ref) => (
  <div ref={ref} className={cn("grid gap-2", className)} role="radiogroup" {...props}>
    {React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _value: value, _onChange: onValueChange }) : c)}
  </div>
));
RadioGroup.displayName = "RadioGroup";
const RadioGroupItem = React.forwardRef<HTMLInputElement, any>(({ className, value, _value, _onChange, ...props }, ref) => (
  <input ref={ref} type="radio" value={value} checked={_value === value} onChange={() => _onChange?.(value)} className={cn("h-4 w-4 border border-primary", className)} {...props} />
));
RadioGroupItem.displayName = "RadioGroupItem";
export { RadioGroup, RadioGroupItem };
`,

  switch: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Switch = React.forwardRef<HTMLButtonElement, any>(({ className, checked, onCheckedChange, ...props }, ref) => (
  <button
    ref={ref}
    role="switch"
    aria-checked={checked}
    onClick={() => onCheckedChange?.(!checked)}
    className={cn("inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors", checked ? "bg-primary" : "bg-input", className)}
    {...props}
  >
    <span className={cn("pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform", checked ? "translate-x-5" : "translate-x-0")} />
  </button>
));
Switch.displayName = "Switch";
export { Switch };
`,

  slider: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Slider = React.forwardRef<HTMLInputElement, any>(({ className, value, defaultValue, onValueChange, min = 0, max = 100, step = 1, ...props }, ref) => (
  <input
    ref={ref}
    type="range"
    min={min}
    max={max}
    step={step}
    value={value?.[0] ?? defaultValue?.[0] ?? 50}
    onChange={(e) => onValueChange?.([Number(e.target.value)])}
    className={cn("w-full h-2 rounded-lg appearance-none cursor-pointer bg-secondary", className)}
    {...props}
  />
));
Slider.displayName = "Slider";
export { Slider };
`,

  progress: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Progress = React.forwardRef<HTMLDivElement, any>(({ className, value = 0, ...props }, ref) => (
  <div ref={ref} className={cn("relative h-4 w-full overflow-hidden rounded-full bg-secondary", className)} {...props}>
    <div className="h-full bg-primary transition-all" style={{ width: \`\${value}%\` }} />
  </div>
));
Progress.displayName = "Progress";
export { Progress };
`,

  skeleton: `// AUTO-GENERATED STUB — will be replaced by AI
import { cn } from "@/lib/utils";

function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("animate-pulse rounded-md bg-muted", className)} {...props} />;
}
export { Skeleton };
`,

  table: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Table = React.forwardRef<HTMLTableElement, any>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto"><table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} /></div>
));
Table.displayName = "Table";
const TableHeader = React.forwardRef<HTMLTableSectionElement, any>(({ className, ...props }, ref) => <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />);
TableHeader.displayName = "TableHeader";
const TableBody = React.forwardRef<HTMLTableSectionElement, any>(({ className, ...props }, ref) => <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />);
TableBody.displayName = "TableBody";
const TableFooter = React.forwardRef<HTMLTableSectionElement, any>(({ className, ...props }, ref) => <tfoot ref={ref} className={cn("border-t bg-muted/50 font-medium", className)} {...props} />);
TableFooter.displayName = "TableFooter";
const TableRow = React.forwardRef<HTMLTableRowElement, any>(({ className, ...props }, ref) => <tr ref={ref} className={cn("border-b transition-colors hover:bg-muted/50", className)} {...props} />);
TableRow.displayName = "TableRow";
const TableHead = React.forwardRef<HTMLTableCellElement, any>(({ className, ...props }, ref) => <th ref={ref} className={cn("h-12 px-4 text-left align-middle font-medium text-muted-foreground", className)} {...props} />);
TableHead.displayName = "TableHead";
const TableCell = React.forwardRef<HTMLTableCellElement, any>(({ className, ...props }, ref) => <td ref={ref} className={cn("p-4 align-middle", className)} {...props} />);
TableCell.displayName = "TableCell";
const TableCaption = React.forwardRef<HTMLTableCaptionElement, any>(({ className, ...props }, ref) => <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />);
TableCaption.displayName = "TableCaption";
export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
`,

  textarea: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn("flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50", className)}
      {...props}
    />
  )
);
Textarea.displayName = "Textarea";
export { Textarea };
`,

  tooltip: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const TooltipProvider = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const Tooltip = ({ children }: { children: React.ReactNode }) => {
  const [show, setShow] = React.useState(false);
  return <span className="relative inline-block" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _show: show }) : c)}</span>;
};
const TooltipTrigger = React.forwardRef<HTMLSpanElement, any>(({ children, asChild, _show, ...props }, ref) => <span ref={ref} {...props}>{children}</span>);
TooltipTrigger.displayName = "TooltipTrigger";
const TooltipContent = React.forwardRef<HTMLDivElement, any>(({ children, className, _show, side, ...props }, ref) => {
  if (!_show) return null;
  return <div ref={ref} className={cn("absolute z-50 rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap", className)} {...props}>{children}</div>;
});
TooltipContent.displayName = "TooltipContent";
export { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent };
`,

  'alert-dialog': `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const AlertDialog = ({ children, open, onOpenChange }: any) => {
  const [isOpen, setIsOpen] = React.useState(open ?? false);
  React.useEffect(() => { if (open !== undefined) setIsOpen(open); }, [open]);
  const toggle = (v: boolean) => { setIsOpen(v); onOpenChange?.(v); };
  return <>{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _open: isOpen, _toggle: toggle }) : c)}</>;
};
const AlertDialogTrigger = ({ children, _toggle, ...props }: any) => <span onClick={() => _toggle?.(true)} {...props}>{children}</span>;
const AlertDialogContent = React.forwardRef<HTMLDivElement, any>(({ children, className, _open, ...props }, ref) => {
  if (!_open) return null;
  return (<><div className="fixed inset-0 z-50 bg-black/80" /><div ref={ref} className={cn("fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background p-6 shadow-lg", className)} {...props}>{children}</div></>);
});
AlertDialogContent.displayName = "AlertDialogContent";
const AlertDialogHeader = ({ className, ...props }: any) => <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />;
const AlertDialogFooter = ({ className, ...props }: any) => <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4", className)} {...props} />;
const AlertDialogTitle = React.forwardRef<HTMLHeadingElement, any>(({ className, ...props }, ref) => <h2 ref={ref} className={cn("text-lg font-semibold", className)} {...props} />);
AlertDialogTitle.displayName = "AlertDialogTitle";
const AlertDialogDescription = React.forwardRef<HTMLParagraphElement, any>(({ className, ...props }, ref) => <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />);
AlertDialogDescription.displayName = "AlertDialogDescription";
const AlertDialogAction = React.forwardRef<HTMLButtonElement, any>(({ className, _toggle, ...props }, ref) => <button ref={ref} className={cn("inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90", className)} onClick={() => _toggle?.(false)} {...props} />);
AlertDialogAction.displayName = "AlertDialogAction";
const AlertDialogCancel = React.forwardRef<HTMLButtonElement, any>(({ className, _toggle, ...props }, ref) => <button ref={ref} className={cn("inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent", className)} onClick={() => _toggle?.(false)} {...props} />);
AlertDialogCancel.displayName = "AlertDialogCancel";
const AlertDialogPortal = ({ children }: any) => <>{children}</>;
const AlertDialogOverlay = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => <div ref={ref} className={cn("fixed inset-0 z-50 bg-black/80", className)} {...props} />);
AlertDialogOverlay.displayName = "AlertDialogOverlay";
export { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel, AlertDialogPortal, AlertDialogOverlay };
`,

  'navigation-menu': `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const NavigationMenu = React.forwardRef<HTMLElement, any>(({ className, children, ...props }, ref) => (
  <nav ref={ref} className={cn("relative z-10 flex max-w-max flex-1 items-center justify-center", className)} {...props}>{children}</nav>
));
NavigationMenu.displayName = "NavigationMenu";
const NavigationMenuList = React.forwardRef<HTMLUListElement, any>(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn("group flex flex-1 list-none items-center justify-center space-x-1", className)} {...props} />
));
NavigationMenuList.displayName = "NavigationMenuList";
const NavigationMenuItem = ({ children, ...props }: any) => <li {...props}>{children}</li>;
const NavigationMenuTrigger = React.forwardRef<HTMLButtonElement, any>(({ className, children, ...props }, ref) => (
  <button ref={ref} className={cn("group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium hover:bg-accent", className)} {...props}>{children}</button>
));
NavigationMenuTrigger.displayName = "NavigationMenuTrigger";
const NavigationMenuContent = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => <div ref={ref} className={cn("absolute left-0 top-full w-full md:w-auto", className)} {...props} />);
NavigationMenuContent.displayName = "NavigationMenuContent";
const NavigationMenuLink = React.forwardRef<HTMLAnchorElement, any>(({ className, ...props }, ref) => <a ref={ref} className={cn("block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent", className)} {...props} />);
NavigationMenuLink.displayName = "NavigationMenuLink";
const NavigationMenuIndicator = () => null;
const NavigationMenuViewport = () => null;
const navigationMenuTriggerStyle = () => "group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium hover:bg-accent";
export { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuTrigger, NavigationMenuContent, NavigationMenuLink, NavigationMenuIndicator, NavigationMenuViewport, navigationMenuTriggerStyle };
`,

  form: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Form = ({ children, ...props }: any) => <form {...props}>{children}</form>;
const FormField = ({ render, name, control }: any) => render?.({ field: { name, value: "", onChange: () => {}, onBlur: () => {}, ref: () => {} } }) || null;
const FormItem = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => <div ref={ref} className={cn("space-y-2", className)} {...props} />);
FormItem.displayName = "FormItem";
const FormLabel = React.forwardRef<HTMLLabelElement, any>(({ className, ...props }, ref) => <label ref={ref} className={cn("text-sm font-medium leading-none", className)} {...props} />);
FormLabel.displayName = "FormLabel";
const FormControl = React.forwardRef<HTMLDivElement, any>(({ ...props }, ref) => <div ref={ref} {...props} />);
FormControl.displayName = "FormControl";
const FormDescription = React.forwardRef<HTMLParagraphElement, any>(({ className, ...props }, ref) => <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />);
FormDescription.displayName = "FormDescription";
const FormMessage = React.forwardRef<HTMLParagraphElement, any>(({ className, children, ...props }, ref) => <p ref={ref} className={cn("text-sm font-medium text-destructive", className)} {...props}>{children}</p>);
FormMessage.displayName = "FormMessage";
const useFormField = () => ({ id: "", name: "", formItemId: "", formDescriptionId: "", formMessageId: "", error: undefined as any });
export { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage, useFormField };
`,

  command: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Command = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground", className)} {...props} />
));
Command.displayName = "Command";
const CommandInput = React.forwardRef<HTMLInputElement, any>(({ className, ...props }, ref) => (
  <div className="flex items-center border-b px-3"><input ref={ref} className={cn("flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground", className)} {...props} /></div>
));
CommandInput.displayName = "CommandInput";
const CommandList = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => <div ref={ref} className={cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className)} {...props} />);
CommandList.displayName = "CommandList";
const CommandEmpty = React.forwardRef<HTMLDivElement, any>((props, ref) => <div ref={ref} className="py-6 text-center text-sm" {...props} />);
CommandEmpty.displayName = "CommandEmpty";
const CommandGroup = React.forwardRef<HTMLDivElement, any>(({ className, heading, children, ...props }, ref) => (
  <div ref={ref} className={cn("overflow-hidden p-1 text-foreground", className)} {...props}>{heading && <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{heading}</div>}{children}</div>
));
CommandGroup.displayName = "CommandGroup";
const CommandItem = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent", className)} {...props} />
));
CommandItem.displayName = "CommandItem";
const CommandSeparator = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => <div ref={ref} className={cn("h-px bg-border", className)} {...props} />);
CommandSeparator.displayName = "CommandSeparator";
const CommandShortcut = ({ className, ...props }: any) => <span className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)} {...props} />;
const CommandDialog = ({ children, open, onOpenChange, ...props }: any) => {
  if (!open) return null;
  return (<><div className="fixed inset-0 z-50 bg-black/80" onClick={() => onOpenChange?.(false)} /><div className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2">{children}</div></>);
};
export { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator, CommandShortcut, CommandDialog };
`,

  collapsible: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";

const Collapsible = ({ children, open, onOpenChange, ...props }: any) => {
  const [isOpen, setIsOpen] = React.useState(open ?? false);
  React.useEffect(() => { if (open !== undefined) setIsOpen(open); }, [open]);
  const toggle = () => { const next = !isOpen; setIsOpen(next); onOpenChange?.(next); };
  return <div {...props}>{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _open: isOpen, _toggle: toggle }) : c)}</div>;
};
const CollapsibleTrigger = ({ children, _toggle, asChild, ...props }: any) => <span onClick={_toggle} style={{ cursor: "pointer" }} {...props}>{children}</span>;
const CollapsibleContent = ({ children, _open, ...props }: any) => _open ? <div {...props}>{children}</div> : null;
export { Collapsible, CollapsibleTrigger, CollapsibleContent };
`,

  'aspect-ratio': `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";

const AspectRatio = React.forwardRef<HTMLDivElement, any>(({ ratio = 16 / 9, children, style, ...props }, ref) => (
  <div ref={ref} style={{ position: "relative", paddingBottom: \`\${100 / ratio}%\`, ...style }} {...props}>
    <div style={{ position: "absolute", inset: 0 }}>{children}</div>
  </div>
));
AspectRatio.displayName = "AspectRatio";
export { AspectRatio };
`,

  'hover-card': `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const HoverCard = ({ children }: any) => {
  const [show, setShow] = React.useState(false);
  return <div className="relative inline-block" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>{React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _show: show }) : c)}</div>;
};
const HoverCardTrigger = React.forwardRef<HTMLAnchorElement, any>(({ children, asChild, _show, ...props }, ref) => <span ref={ref as any} {...props}>{children}</span>);
HoverCardTrigger.displayName = "HoverCardTrigger";
const HoverCardContent = React.forwardRef<HTMLDivElement, any>(({ children, className, _show, ...props }, ref) => {
  if (!_show) return null;
  return <div ref={ref} className={cn("absolute z-50 w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md", className)} {...props}>{children}</div>;
});
HoverCardContent.displayName = "HoverCardContent";
export { HoverCard, HoverCardTrigger, HoverCardContent };
`,

  toggle: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Toggle = React.forwardRef<HTMLButtonElement, any>(({ className, pressed, onPressedChange, variant, size, children, ...props }, ref) => (
  <button
    ref={ref}
    aria-pressed={pressed}
    onClick={() => onPressedChange?.(!pressed)}
    className={cn("inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted hover:text-muted-foreground", pressed && "bg-accent text-accent-foreground", size === "sm" ? "h-9 px-2.5" : size === "lg" ? "h-11 px-5" : "h-10 px-3", className)}
    {...props}
  >{children}</button>
));
Toggle.displayName = "Toggle";
const toggleVariants = () => "";
export { Toggle, toggleVariants };
`,

  'toggle-group': `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const ToggleGroup = React.forwardRef<HTMLDivElement, any>(({ className, type, value, onValueChange, children, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center justify-center gap-1", className)} {...props}>
    {React.Children.map(children, (c) => React.isValidElement(c) ? React.cloneElement(c as any, { _value: value, _onChange: onValueChange, _type: type }) : c)}
  </div>
));
ToggleGroup.displayName = "ToggleGroup";
const ToggleGroupItem = React.forwardRef<HTMLButtonElement, any>(({ className, value, _value, _onChange, _type, children, ...props }, ref) => {
  const isActive = _type === "multiple" ? Array.isArray(_value) && _value.includes(value) : _value === value;
  return <button ref={ref} aria-pressed={isActive} className={cn("inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted h-10 px-3", isActive && "bg-accent text-accent-foreground", className)} onClick={() => _onChange?.(value)} {...props}>{children}</button>;
});
ToggleGroupItem.displayName = "ToggleGroupItem";
export { ToggleGroup, ToggleGroupItem };
`,

  breadcrumb: `// AUTO-GENERATED STUB — will be replaced by AI
import * as React from "react";
import { cn } from "@/lib/utils";

const Breadcrumb = React.forwardRef<HTMLElement, any>(({ ...props }, ref) => <nav ref={ref} aria-label="breadcrumb" {...props} />);
Breadcrumb.displayName = "Breadcrumb";
const BreadcrumbList = React.forwardRef<HTMLOListElement, any>(({ className, ...props }, ref) => <ol ref={ref} className={cn("flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5", className)} {...props} />);
BreadcrumbList.displayName = "BreadcrumbList";
const BreadcrumbItem = React.forwardRef<HTMLLIElement, any>(({ className, ...props }, ref) => <li ref={ref} className={cn("inline-flex items-center gap-1.5", className)} {...props} />);
BreadcrumbItem.displayName = "BreadcrumbItem";
const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, any>(({ className, asChild, ...props }, ref) => <a ref={ref} className={cn("transition-colors hover:text-foreground", className)} {...props} />);
BreadcrumbLink.displayName = "BreadcrumbLink";
const BreadcrumbPage = React.forwardRef<HTMLSpanElement, any>(({ className, ...props }, ref) => <span ref={ref} className={cn("font-normal text-foreground", className)} {...props} />);
BreadcrumbPage.displayName = "BreadcrumbPage";
const BreadcrumbSeparator = ({ children, className, ...props }: any) => <li className={cn("[&>svg]:size-3.5", className)} {...props}>{children || "/"}</li>;
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";
const BreadcrumbEllipsis = ({ className, ...props }: any) => <span className={cn("flex h-9 w-9 items-center justify-center", className)} {...props}>...</span>;
BreadcrumbEllipsis.displayName = "BreadcrumbEllipsis";
export { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator, BreadcrumbEllipsis };
`,
};

// Get set of all component names in the stub registry for quick lookup
export function getShadcnStubNames(): Set<string> {
  return new Set(Object.keys(SHADCN_STUB_REGISTRY));
}

// ── Helpers ──

function resolveRelativePath(fileDir: string, importPath: string): string {
  if (importPath.startsWith('./')) {
    return (fileDir + '/' + importPath.slice(2)).replace(/\/+/g, '/').replace(/^\.\//, '');
  }
  if (importPath.startsWith('../')) {
    const parts = fileDir.split('/');
    let remaining = importPath;
    while (remaining.startsWith('../')) {
      parts.pop();
      remaining = remaining.slice(3);
    }
    return (parts.join('/') + '/' + remaining).replace(/\/+/g, '/').replace(/^\.\//, '');
  }
  return importPath;
}

function getLineContaining(code: string, index: number): string {
  const start = code.lastIndexOf('\n', index) + 1;
  const end = code.indexOf('\n', index);
  return code.slice(start, end === -1 ? undefined : end);
}

function toPascalCase(str: string): string {
  return str
    .replace(/[-_](.)/g, (_, c) => c.toUpperCase())
    .replace(/^(.)/, (_, c) => c.toUpperCase());
}
